import { forwardRef } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import NumberFormat, { NumberFormatProps as ReactNumberFormatProps } from 'react-number-format';

import { InputMaskTypes } from './enums';

export const PHONE_NUMBER_MASK = '(###) ### - ####';
export const CREDIT_CARD_MASK = '#### #### #### ####';
export const CREDIT_CARD_EXPIRY_MASK = '##/##';
export const SSN_MASK = '###-##-####';

export const maskPropTypes = [
  InputMaskTypes.Phone,
  InputMaskTypes.CreditCard,
  InputMaskTypes.CreditCardExpiry,
  InputMaskTypes.SSN,
  InputMaskTypes.Currency,
  InputMaskTypes.Custom
];

export const inputMaskPropTypes = {
  /**
   * This is used to determine which mask to use. Can be one of the following: 'phone', 'creditCard', 'creditCardExpiry', 'ssn', 'currency', or 'custom'.
   */
  mask: PropTypes.oneOf(maskPropTypes).isRequired,
  /**
   * Can be paired with the mask prop (i.e mask="custom") to create a custom mask using react-number-format directly.
   */
  customPattern: PropTypes.objectOf(PropTypes.any)
};

export type InputMaskProps = {
  mask: string;
  customOptions?: unknown;
} & ReactNumberFormatProps;

const numberFormatPropsMap: Record<string, Partial<ReactNumberFormatProps>> = {
  [InputMaskTypes.Phone]: {
    format: PHONE_NUMBER_MASK
  },
  [InputMaskTypes.CreditCard]: {
    format: CREDIT_CARD_MASK
  },
  [InputMaskTypes.CreditCardExpiry]: {
    format: CREDIT_CARD_EXPIRY_MASK
  },
  [InputMaskTypes.Currency]: {
    decimalSeparator: '.',
    decimalScale: 2,
    allowNegative: false,
    thousandSeparator: true
  },
  [InputMaskTypes.SSN]: {
    format: SSN_MASK
  }
};

/**
 * Internal utility component used in the Input component that makes working with input masks easier.
 * This component is not exported by the core library.
 *
 * @private
 * @component
 * @example
 * return (
 *   <InputMask mask="creditCard"/>
 * )
 */
const InputMask = forwardRef<InputMaskProps, 'input'>((props: InputMaskProps, ref) => {
  const { mask, customOptions, onBlur, onFocus, ...inputPropTypes } = props;

  const [fixedDecimalScale, setFixedDecimalScale] = useState(false);

  const handleOnBlur = (evt: React.FocusEvent<HTMLInputElement, Element>) => {
    if (mask === InputMaskTypes.Currency) {
      setFixedDecimalScale(true);
    }

    if (onBlur) {
      onBlur(evt);
    }
  };

  const handleOnFocus = (evt: React.FocusEvent<HTMLInputElement, Element>) => {
    if (mask === InputMaskTypes.Currency) {
      setFixedDecimalScale(false);
    }

    if (onFocus) {
      onFocus(evt);
    }
  };

  return mask === InputMaskTypes.Custom ? (
    <NumberFormat {...customOptions} onBlur={onBlur} ref={ref} {...inputPropTypes} />
  ) : (
    <NumberFormat
      {...numberFormatPropsMap[mask]}
      fixedDecimalScale={fixedDecimalScale}
      onBlur={handleOnBlur}
      onFocus={handleOnFocus}
      ref={ref}
      {...inputPropTypes}
    />
  );
});

export default InputMask;
